import React from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
} from '@mui/material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import type { MissingWarehouseProduct } from '../utils/warehouseStockProvisioning';

/**
 * Hedef depoda stok satırı olmayan ürünleri listeler ve 1 adetlik tanım açmayı
 * teklif eder. Taslak uygulama ve depo değiştirme akışları bu diyalogu paylaşır;
 * iki yerde de aynı kural geçerli: kaynak depodan miktar düşülmez.
 */

/** Listede tek seferde gösterilecek satır sayısı. */
const PREVIEW_LIMIT = 50;

interface MissingWarehouseStockDialogProps {
  open: boolean;
  warehouseName: string;
  products: MissingWarehouseProduct[];
  creating: boolean;
  onSkip: () => void;
  onCreate: () => void;
}

const MissingWarehouseStockDialog: React.FC<MissingWarehouseStockDialogProps> = ({
  open,
  warehouseName,
  products,
  creating,
  onSkip,
  onCreate,
}) => {
  const preview = products.slice(0, PREVIEW_LIMIT);
  const rest = products.length - preview.length;

  return (
    <Dialog
      open={open}
      onClose={() => {
        if (!creating) onSkip();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Inventory2Icon color="warning" />
        Bu ürünler seçili depoda tanımlı değil
      </DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          {products.length} ürünün "{warehouseName}" deposunda stok kaydı yok. Satış yapıldığında bu
          kayıtlar otomatik açılır ve stok eksiye düşer; istersen şimdi 1 adetle tanımlayabilirim. Bu
          bir transfer değildir — ürünlerin diğer depolardaki miktarları değişmez.
        </Alert>
        <List dense sx={{ maxHeight: 280, overflowY: 'auto', bgcolor: 'action.hover', borderRadius: 1 }}>
          {preview.map((product) => (
            <ListItem key={product.id} divider>
              <ListItemText primary={product.name} secondary={product.stockCode || undefined} />
            </ListItem>
          ))}
        </List>
        {rest > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            ve {rest} ürün daha…
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onSkip} color="inherit" disabled={creating}>
          Şimdilik atla
        </Button>
        <Button
          onClick={onCreate}
          variant="contained"
          disabled={creating}
          startIcon={creating ? <CircularProgress size={14} color="inherit" /> : null}
        >
          Depoda oluştur (1 adet)
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MissingWarehouseStockDialog;
